import { useApiQuery } from '../lib/useApiQuery';
import { formatExportRows, formatDate as format, formatTime, formatName } from '../lib/format';
import SectionCard from '../components/ui/SectionCard';
import PageContent from '../components/ui/PageContent';
import PageHeader from '../components/ui/PageHeader';
import { TableEmpty, Table, TableHead, TableRow, TableHeaderCell, TableCell } from '../components/ui/Table';
import FieldLabel from '../components/ui/FieldLabel';
import UiInput from '../components/ui/Input';
import UiSelect from '../components/ui/Select';
import Button from '../components/ui/Button';
import { withDownloadProgress } from '../utils/downloadProgress';
import TimesheetChangeHistory from '../components/TimesheetChangeHistory';
import { useEffect, useMemo, useRef, useState } from "react";
import Header from "../components/Header";
import Sidebar from "../components/Sidebar";
import useTableSort from "../components/useTableSort";
import { getDropdownLabel, getQuarterHoursFromTimes, postWithAuth } from "../common";
import { toast } from "react-toastify";
import { useSelector } from "react-redux";
import MyCalendar from "../components/Calendar";
import Checkbox from "../components/Checkbox";
import Input from "../components/Input";
import { addWeeks, endOfWeek, startOfWeek, subWeeks } from 'date-fns';
import { useLocation, useNavigate } from "react-router-dom";
import { getPrimaryWorkerType, getWorkerRateStructures, inferWorkerUserType, normalizeContractorRates, normalizeEmployeeRates } from "../utils/workerRates";
import Section from "../components/Section";
import Dropdown from "../components/Dropdown";
import ModalComponent from "../components/Modal";
import LoadingPopup from "../components/LoadingPopup";
import TimesheetPdfGenerator from "../components/TimesheetPdfGenerator";
import { getAllocationDisplayAddress } from '../utils/workerAllocationAddress';
import { businessWallDateToIso, toBusinessWallDate } from '../utils/businessTime';
const contractorRateLabels = {
    weekdayMorningsRate: 'Monday to Friday from 6am to 6pm',
    weekdayNightsRate: 'Monday to Friday from 6pm to 6am',
    fridayNightRate: 'Friday to Saturday from 6pm to 6am',
    saturdayDayRate: 'Saturday from 6am to 6pm',
    saturdayNightRate: 'Saturday to Sunday from 6pm to 6am',
    sundayDayRate: 'Sunday from 6am to 6pm',
    sundayNightRate: 'Sunday to Monday from 6pm to 6am',
}

const employeeRateLabels = {
    weekdayBaseRate: 'Weekday base (first 8 hours)',
    weekdayOvertime0To2Rate: 'Weekday overtime 0-2 hours',
    weekdayOvertime2PlusRate: 'Weekday overtime 2+ hours',
    saturdayFirst2Rate: 'Saturday first 2 hours',
    saturday2PlusRate: 'Saturday 2+ hours',
    sundayAllDayRate: 'Sunday all day',
    mealAllowance: 'Meal allowance',
    travelAllowance: 'Travel allowance',
}

const timesheetStatusOptions = [
    { label: 'Completed', value: 'Completed' },
    { label: 'Incomplete', value: 'Incomplete' },
    { label: 'Accepted', value: 'Accepted' },
]

const breakMinuteOptions = Array.from({ length: 17 }, (_, index) => {
    const minutes = index * 15
    return { label: String(minutes), value: String(minutes) }
})

const getTimesheetDisplayStatus = (entry) => {
    const status = String(entry?.status || '').toLowerCase()
    const today = toBusinessWallDate(new Date())
    today.setHours(0, 0, 0, 0)
    const allocationDate = toBusinessWallDate(entry?.date)
    if (entry?.clockIn && entry?.clockOut) return 'Completed'
    if (entry?.clockIn && !entry?.clockOut) return 'Incomplete'
    if (status === 'completed') return 'Completed'
    if (status === 'active') return 'Incomplete'
    if (status === 'accepted' && allocationDate && allocationDate < today) return 'Incomplete'
    if (status === 'accepted' || status === 'approved') return 'Accepted'
    return 'Incomplete'
}

const toNumber = (value) => {
    const num = Number(value)
    return Number.isFinite(num) ? num : 0
}

const escapeCsvValue = (value) => {
    const stringValue = String(value ?? '')
    if (/[",\n]/.test(stringValue)) {
        return `"${stringValue.replace(/"/g, '""')}"`
    }
    return stringValue
}

const downloadCsvFile = (rows, fileNamePrefix) => withDownloadProgress(async () => {
    const csvContent = formatExportRows(rows)
        .map(row => row.map(value => escapeCsvValue(value)).join(','))
        .join('\r\n')
    const blob = new Blob(["\uFEFF" + csvContent], { type: 'text/csv;charset=utf-8;' })
    const url = URL.createObjectURL(blob)
    const link = document.createElement('a')
    link.href = url
    link.download = `${fileNamePrefix}_${format(new Date(), 'yyyyMMdd_HHmm')}.csv`
    document.body.appendChild(link)
    link.click()
    link.remove()
    URL.revokeObjectURL(url)
})

const getScheduleKeyFromStart = (clockIn) => {
    if (!clockIn) return 'day'
    const melbourneParts = getMelbourneDateParts(clockIn)
    const hour = melbourneParts?.hour ?? new Date(clockIn).getHours()
    return hour >= 6 && hour < 18 ? 'day' : 'night'
}

const MELBOURNE_TIMEZONE = 'Australia/Sydney'
const melbourneDateFormatter = new Intl.DateTimeFormat('en-AU', {
    timeZone: MELBOURNE_TIMEZONE,
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit',
    hour12: false,
    weekday: 'short',
})

const melbourneWeekdayMap = {
    Sun: 0,
    Mon: 1,
    Tue: 2,
    Wed: 3,
    Thu: 4,
    Fri: 5,
    Sat: 6,
}

const getMelbourneDateParts = (value) => {
    const date = value instanceof Date ? value : new Date(value)
    if (Number.isNaN(date.getTime())) return null
    const parts = melbourneDateFormatter.formatToParts(date)
    const getPart = (type) => parts.find((part) => part.type === type)?.value || ''
    return {
        year: Number(getPart('year')),
        month: Number(getPart('month')),
        day: Number(getPart('day')),
        hour: Number(getPart('hour')),
        minute: Number(getPart('minute')),
        second: Number(getPart('second')),
        weekday: melbourneWeekdayMap[getPart('weekday')] ?? 0,
    }
}

const melbourneLocalToDate = ({ year, month, day, hour = 0, minute = 0, second = 0 }) => {
    let guess = new Date(Date.UTC(year, month - 1, day, hour, minute, second))
    for (let index = 0; index < 4; index += 1) {
        const currentParts = getMelbourneDateParts(guess)
        if (!currentParts) return guess
        const desiredUtcMs = Date.UTC(year, month - 1, day, hour, minute, second)
        const currentLocalAsUtcMs = Date.UTC(
            currentParts.year,
            currentParts.month - 1,
            currentParts.day,
            currentParts.hour,
            currentParts.minute,
            currentParts.second,
        )
        const diffMs = desiredUtcMs - currentLocalAsUtcMs
        if (diffMs === 0) return guess
        guess = new Date(guess.getTime() + diffMs)
    }
    return guess
}

const getContractorRateFieldForDate = (date) => {
    if (!date) return 'weekdayMorningsRate'
    const melbourneParts = getMelbourneDateParts(date)
    if (!melbourneParts) return 'weekdayMorningsRate'
    const day = melbourneParts.weekday
    const hour = melbourneParts.hour
    const isNight = hour >= 18 || hour < 6
    const bandDay = isNight && hour < 6 ? (day + 6) % 7 : day

    if (bandDay === 5 && isNight) return 'fridayNightRate'
    if (bandDay === 6) return isNight ? 'saturdayNightRate' : 'saturdayDayRate'
    if (bandDay === 0) return isNight ? 'sundayNightRate' : 'sundayDayRate'
    return isNight ? 'weekdayNightsRate' : 'weekdayMorningsRate'
}

const getNextContractorRateBoundary = (current) => {
    const melbourneParts = getMelbourneDateParts(current)
    if (!melbourneParts) return new Date(current)
    const { year, month, day, hour, minute, second } = melbourneParts
    const minutesIntoDay = (hour * 60) + minute + (second / 60)

    if (minutesIntoDay < 6 * 60) {
        return melbourneLocalToDate({ year, month, day, hour: 6, minute: 0, second: 0 })
    }

    if (minutesIntoDay < 18 * 60) {
        return melbourneLocalToDate({ year, month, day, hour: 18, minute: 0, second: 0 })
    }

    const nextDay = new Date(Date.UTC(year, month - 1, day))
    nextDay.setUTCDate(nextDay.getUTCDate() + 1)
    return melbourneLocalToDate({
        year: nextDay.getUTCFullYear(),
        month: nextDay.getUTCMonth() + 1,
        day: nextDay.getUTCDate(),
        hour: 6,
        minute: 0,
        second: 0,
    })
}

const getWorkerPayrollInfo = (entry, workerDetails) => {
    const details = workerDetails || {}
    const entryRates = entry.workerRates || {}
    return {
        userType: entry.workerUserType || inferWorkerUserType(details),
        workerTypes: Array.isArray(entry.workerTypes) && entry.workerTypes.length > 0
            ? entry.workerTypes
            : (Array.isArray(details.workerType) ? details.workerType : []),
        workerCapacity: entry.workerCapacity || '',
        contractorRates: entryRates.contractorRates
            ? normalizeContractorRates(entryRates.contractorRates)
            : getWorkerRateStructures(details).contractorRates,
        employeeRates: entryRates.employeeRates
            ? normalizeEmployeeRates(entryRates.employeeRates)
            : getWorkerRateStructures(details).employeeRates,
    }
}

const applyUnpaidBreakToSegments = (segments, breakMinutes) => {
    let remainingBreakMinutes = Math.max(0, toNumber(breakMinutes))
    return segments.map((segment) => {
        if (remainingBreakMinutes <= 0) {
            return { ...segment, unpaidBreakMinutes: 0, paidMinutes: segment.minutes }
        }
        const deducted = Math.min(segment.minutes, remainingBreakMinutes)
        remainingBreakMinutes -= deducted
        return {
            ...segment,
            unpaidBreakMinutes: deducted,
            paidMinutes: Math.max(0, segment.minutes - deducted),
        }
    })
}

const splitShiftByMidnight = (entry) => {
    if (!entry.clockIn || !entry.clockOut) {
        return []
    }

    const start = new Date(entry.clockIn)
    const end = new Date(entry.clockOut)
    if (Number.isNaN(start.getTime()) || Number.isNaN(end.getTime()) || start >= end) {
        return []
    }

    const segments = []
    let cursor = new Date(start)
    while (cursor < end) {
        const nextBoundary = new Date(cursor)
        nextBoundary.setHours(24, 0, 0, 0)
        const segmentEnd = nextBoundary < end ? nextBoundary : end
        const minutes = Math.max(0, (segmentEnd - cursor) / (1000 * 60))
        segments.push({
            clockIn: new Date(cursor),
            clockOut: new Date(segmentEnd),
            minutes,
        })
        cursor = new Date(segmentEnd)
    }

    const unpaidBreakMinutes = entry.breakIncludedInPay === false ? toNumber(entry.breakMinutes) : 0
    return applyUnpaidBreakToSegments(segments, unpaidBreakMinutes)
}

const splitShiftByContractorRate = (entry, workerPayrollInfo) => {
    if (!entry.clockIn || !entry.clockOut) {
        const startDate = entry.clockIn || entry.date || entry.clockOut
        const rateField = getContractorRateFieldForDate(startDate)
        const capacity = workerPayrollInfo.workerCapacity || getPrimaryWorkerType(workerPayrollInfo.workerTypes)
        return [{
            clockIn: entry.clockIn,
            clockOut: entry.clockOut,
            rateField,
            rate: capacity ? toNumber(workerPayrollInfo.contractorRates?.[rateField]?.[capacity]) : 0,
            capacity,
            minutes: 0,
            unpaidBreakMinutes: 0,
            paidMinutes: 0,
        }]
    }

    const start = new Date(entry.clockIn)
    const end = new Date(entry.clockOut)
    if (Number.isNaN(start.getTime()) || Number.isNaN(end.getTime()) || start >= end) {
        const startDate = entry.clockIn || entry.date || entry.clockOut
        const rateField = getContractorRateFieldForDate(startDate)
        const capacity = workerPayrollInfo.workerCapacity || getPrimaryWorkerType(workerPayrollInfo.workerTypes)
        return [{
            clockIn: entry.clockIn,
            clockOut: entry.clockOut,
            rateField,
            rate: capacity ? toNumber(workerPayrollInfo.contractorRates?.[rateField]?.[capacity]) : 0,
            capacity,
            minutes: 0,
            unpaidBreakMinutes: 0,
            paidMinutes: 0,
        }]
    }

    const capacity = workerPayrollInfo.workerCapacity || getPrimaryWorkerType(workerPayrollInfo.workerTypes)
    const segments = []
    let cursor = new Date(start)
    while (cursor < end) {
        const nextBoundary = getNextContractorRateBoundary(cursor)
        const segmentEnd = nextBoundary < end ? nextBoundary : end
        const minutes = Math.max(0, (segmentEnd - cursor) / (1000 * 60))
        const rateField = getContractorRateFieldForDate(cursor)
        segments.push({
            clockIn: new Date(cursor),
            clockOut: new Date(segmentEnd),
            rateField,
            rate: capacity ? toNumber(workerPayrollInfo.contractorRates?.[rateField]?.[capacity]) : 0,
            capacity,
            minutes,
        })
        cursor = new Date(segmentEnd)
    }

    const unpaidBreakMinutes = entry.breakIncludedInPay === false ? toNumber(entry.breakMinutes) : 0
    return applyUnpaidBreakToSegments(segments, unpaidBreakMinutes)
}

const splitSegmentByScheduleBoundary = (segment) => {
    if (!segment?.clockIn || !segment?.clockOut) {
        return []
    }

    const start = new Date(segment.clockIn)
    const end = new Date(segment.clockOut)
    if (Number.isNaN(start.getTime()) || Number.isNaN(end.getTime()) || start >= end) {
        return []
    }

    const subSegments = []
    let cursor = new Date(start)
    while (cursor < end) {
        const nextBoundary = getNextContractorRateBoundary(cursor)
        const segmentEnd = nextBoundary < end ? nextBoundary : end
        const minutes = Math.max(0, (segmentEnd - cursor) / (1000 * 60))
        subSegments.push({
            clockIn: new Date(cursor),
            clockOut: new Date(segmentEnd),
            minutes,
        })
        cursor = new Date(segmentEnd)
    }

    return applyUnpaidBreakToSegments(subSegments, segment.unpaidBreakMinutes || 0)
}

const buildEmployeePayRows = (entry, workerPayrollInfo) => {
    const daySegments = splitShiftByMidnight(entry)
    const segments = daySegments.flatMap(splitSegmentByScheduleBoundary)
    const rows = []
    let weekdayPaidMinutesUsed = 0
    let weekdayOvertimeMinutes = 0
    let saturdayPaidMinutesUsed = 0

    const addHourlyRow = ({ label, rateKey, minutes, clockIn, clockOut, rates }) => {
        const paidMinutes = Math.max(0, minutes)
        if (paidMinutes <= 0) return
        rows.push({
            rowType: label,
            rate: toNumber(rates[rateKey]),
            hours: paidMinutes / 60,
            payment: (paidMinutes / 60) * toNumber(rates[rateKey]),
            clockIn,
            clockOut,
        })
    }

    segments.forEach((segment) => {
        const paidMinutes = Math.max(0, segment.paidMinutes || 0)
        if (paidMinutes <= 0) return
        const scheduleKey = getScheduleKeyFromStart(segment.clockIn)
        const rates = workerPayrollInfo.employeeRates?.[scheduleKey] || {}
        const day = getMelbourneDateParts(segment.clockIn)?.weekday ?? new Date(segment.clockIn).getDay()

        if (day >= 1 && day <= 5) {
            let remainingMinutes = paidMinutes
            const baseMinutes = Math.min(remainingMinutes, Math.max(0, 480 - weekdayPaidMinutesUsed))
            weekdayPaidMinutesUsed += baseMinutes
            remainingMinutes -= baseMinutes

            const overtime0To2Minutes = Math.min(remainingMinutes, Math.max(0, 600 - weekdayPaidMinutesUsed))
            weekdayPaidMinutesUsed += overtime0To2Minutes
            remainingMinutes -= overtime0To2Minutes

            const overtime2PlusMinutes = Math.max(0, remainingMinutes)
            weekdayPaidMinutesUsed += overtime2PlusMinutes

            addHourlyRow({
                label: employeeRateLabels.weekdayBaseRate,
                rateKey: 'weekdayBaseRate',
                minutes: baseMinutes,
                clockIn: segment.clockIn,
                clockOut: segment.clockOut,
                rates,
            })
            addHourlyRow({
                label: employeeRateLabels.weekdayOvertime0To2Rate,
                rateKey: 'weekdayOvertime0To2Rate',
                minutes: overtime0To2Minutes,
                clockIn: segment.clockIn,
                clockOut: segment.clockOut,
                rates,
            })
            addHourlyRow({
                label: employeeRateLabels.weekdayOvertime2PlusRate,
                rateKey: 'weekdayOvertime2PlusRate',
                minutes: overtime2PlusMinutes,
                clockIn: segment.clockIn,
                clockOut: segment.clockOut,
                rates,
            })

            weekdayOvertimeMinutes += overtime0To2Minutes + overtime2PlusMinutes
            return
        }

        if (day === 6) {
            let remainingMinutes = paidMinutes
            const first2Minutes = Math.min(remainingMinutes, Math.max(0, 120 - saturdayPaidMinutesUsed))
            saturdayPaidMinutesUsed += first2Minutes
            remainingMinutes -= first2Minutes
            const extraMinutes = Math.max(0, remainingMinutes)
            saturdayPaidMinutesUsed += extraMinutes
            addHourlyRow({
                label: employeeRateLabels.saturdayFirst2Rate,
                rateKey: 'saturdayFirst2Rate',
                minutes: first2Minutes,
                clockIn: segment.clockIn,
                clockOut: segment.clockOut,
                rates,
            })
            addHourlyRow({
                label: employeeRateLabels.saturday2PlusRate,
                rateKey: 'saturday2PlusRate',
                minutes: extraMinutes,
                clockIn: segment.clockIn,
                clockOut: segment.clockOut,
                rates,
            })
            return
        }

        addHourlyRow({
            label: employeeRateLabels.sundayAllDayRate,
            rateKey: 'sundayAllDayRate',
            minutes: paidMinutes,
            clockIn: segment.clockIn,
            clockOut: segment.clockOut,
            rates,
        })
    })

    const finalRates = workerPayrollInfo.employeeRates?.[getScheduleKeyFromStart(entry.clockIn)] || {}
    if (weekdayOvertimeMinutes >= 90 && toNumber(finalRates.mealAllowance) > 0) {
        rows.push({
            rowType: employeeRateLabels.mealAllowance,
            rate: toNumber(finalRates.mealAllowance),
            hours: '',
            payment: toNumber(finalRates.mealAllowance),
            clockIn: entry.clockIn,
            clockOut: entry.clockOut,
        })
    }

    if (entry.travelAllowanceApplied && toNumber(finalRates.travelAllowance) > 0) {
        rows.push({
            rowType: employeeRateLabels.travelAllowance,
            rate: toNumber(finalRates.travelAllowance),
            hours: '',
            payment: toNumber(finalRates.travelAllowance),
            clockIn: entry.clockIn,
            clockOut: entry.clockOut,
        })
    }

    return rows
}


const Timesheet = () => {

    const [loading, setLoading] = useState(false)
    const [isAdding, setIsAdding] = useState(false)
    const [timesheet, setTimeSheet] = useState([])
    const [clients, setClients] = useState([])
    const [sites, setSites] = useState([])
    const [workers, setWorkers] = useState([])
    const [assets, setAssets] = useState([])
    const [statsDate, setStatsDate] = useState(() => toBusinessWallDate(new Date()))

    const [editId, setEditId] = useState('')

    const [viewTimeSheet, setViewTimeSheet] = useState('')
    const [downloadTimeSheet, setDownloadTimeSheet] = useState(null)
    const [clockIn, setClockIn] = useState('')
    const [clockOut, setClockOut] = useState('')
    const [breakMinutes, setBreakMinutes] = useState('0')
    const [breakIncludedInPay, setBreakIncludedInPay] = useState(false)
    const [travelAllowanceApplied, setTravelAllowanceApplied] = useState(false)
    const [isTimesheetDirty, setIsTimesheetDirty] = useState(false)
    const [showTimesheetSuccess, setShowTimesheetSuccess] = useState(false)
    const [reportStartDate, setReportStartDate] = useState('')
    const [reportEndDate, setReportEndDate] = useState('')
    const [timesheetClientFilter, setTimesheetClientFilter] = useState('')
    const [timesheetSiteFilter, setTimesheetSiteFilter] = useState('')
    const [timesheetStatusFilter, setTimesheetStatusFilter] = useState('')

    const userInfo = useSelector((state) => state.user.user)
    const [reportType, setReportType] = useState(userInfo?.isStaff ? 'client' : 'worker')
    const activeOrg = useSelector((state) => state.user.activeOrg)
    const activeOrgInfo = useSelector((state) => state.user.activeOrgInfo)
    const location = useLocation()
    const navigate = useNavigate()
    const lastOpenedRef = useRef(null)
    const timesheetQuery = useApiQuery('fetchTimeSheet', {
        orgId: activeOrg?.id,
        rangeStart: businessWallDateToIso(startOfWeek(statsDate, { weekStartsOn: 1 })),
        rangeEnd: businessWallDateToIso(endOfWeek(statsDate, { weekStartsOn: 1 })),
    }, { enabled: !!activeOrg?.id })
    useEffect(() => { setTimeSheet(timesheetQuery.data?.data || []) }, [timesheetQuery.data])
    useEffect(() => { if (timesheetQuery.error) toast.error(timesheetQuery.error.message) }, [timesheetQuery.error])



    useEffect(() => {
        if (activeOrg) {
            fetchClients()
            fetchWorkers()
            fetchAssets()
            fetchSites()

        }
    }, [activeOrg])

    useEffect(() => {
        const requestedStatus = location.state?.timesheetStatus
        const requestedWeekStart = location.state?.weekStart

        if (requestedStatus) {
            const normalizedRequestedStatus = requestedStatus === 'Approved' ? 'Accepted' : requestedStatus
            setTimesheetStatusFilter(timesheetStatusOptions.find(option => option.value === normalizedRequestedStatus) || '')
        }
        if (requestedWeekStart) {
            const requestedDate = new Date(requestedWeekStart)
            if (!Number.isNaN(requestedDate.getTime())) {
                setStatsDate(startOfWeek(requestedDate, { weekStartsOn: 1 }))
            }
        }
    }, [location.state])

    useEffect(() => {
        const state = location.state
        if (!activeOrg?.id || timesheetQuery.isPending) return

        const queryTimeSheetId = new URLSearchParams(location.search).get('timesheetId')
        const openTimeSheetId = queryTimeSheetId || state?.openTimeSheetId
        if (!openTimeSheetId || lastOpenedRef.current === openTimeSheetId) return

        const target = timesheet.find(entry => entry.id === openTimeSheetId)
        if (target) {
            handleView(target, { syncUrl: false })
            lastOpenedRef.current = openTimeSheetId
        } else {
            lastOpenedRef.current = openTimeSheetId
            let cancelled = false
            fetchTimeSheetDetails({ timeSheetId: openTimeSheetId }).then(rows => {
                if (!cancelled && rows[0]) handleView(rows[0], { syncUrl: false })
            }).catch(error => { if (!cancelled) toast.error(error.message) })
            return () => { cancelled = true; lastOpenedRef.current = null }
        }
    }, [location.search, location.state, timesheet, clients, sites, workers])

    // timesheetBackClose: close when the param disappears (browser Back).
    useEffect(() => {
        const id = new URLSearchParams(location.search).get('timesheetId')
        if (!id && viewTimeSheet && isAdding) {
            closeTimesheetView()
        }
    }, [location.search])

    const fetchTimeSheet = () => timesheetQuery.refetch()

    const fetchTimeSheetDetails = async ({ timeSheetId, allocationId }) => {
        const resp = await postWithAuth('fetchTimeSheet', { orgId: activeOrg.id, timeSheetId, allocationId })
        if (resp && resp.err) {
            throw new Error(resp.err)
        }
        return resp?.data || []
    }


    const fetchClients = async () => {

        try {

            const resp = await postWithAuth('fetchClients', { orgId: activeOrg.id, listOnly: true })

            if (resp && resp.err) {
                toast.error(resp.err)
                return
            }

            if (resp && resp.data) {
                setClients(resp.data)
            }
        } catch (e) {
            toast.error(e.message)
        }
    }

    const fetchSites = async () => {

        try {

            const resp = await postWithAuth('fetchSites', { orgId: activeOrg.id, listOnly: true })

            if (resp && resp.err) {
                toast.error(resp.err)
                return
            }

            if (resp && resp.data) {
                setSites(resp.data)
            }
        } catch (e) {
            toast.error(e.message)
        }
    }

    const fetchWorkers = async () => {

        try {

            const resp = await postWithAuth('fetchWorkers', {
                orgId: activeOrg.id,
                includeArchived: activeOrg?.role === 'admin',
                listOnly: true,
            })

            if (resp && resp.err) {
                toast.error(resp.err)
                return
            }

            if (resp && resp.data) {
                setWorkers(resp.data)
            }
        } catch (e) {
            toast.error(e.message)
        }
    }


    const fetchAssets = async () => {

        try {

            const resp = await postWithAuth('fetchAssets', { orgId: activeOrg.id })

            if (resp && resp.err) {
                toast.error(resp.err)
                return
            }

            if (resp && resp.data) {
                setAssets(resp.data)
            }
        } catch (e) {
            toast.error(e.message)
        }
    }

    const weekRange = useMemo(() => ({
        start: startOfWeek(statsDate, { weekStartsOn: 1 }),
        end: endOfWeek(statsDate, { weekStartsOn: 1 }),
    }), [statsDate])
    const currentWeekStart = useMemo(() => startOfWeek(new Date(), { weekStartsOn: 1 }), [])
    const canGoNextWeek = startOfWeek(statsDate, { weekStartsOn: 1 }).getTime() < currentWeekStart.getTime()
    const clientOptions = useMemo(
        () => clients.map(client => ({ label: client.name, value: client.id })),
        [clients]
    )
    const siteOptions = useMemo(
        () => sites.map(site => ({ label: site.name, value: site.id })),
        [sites]
    )
    const workerOptions = useMemo(
        () => workers.map(worker => ({ label: formatName(`${worker.firstName || ''} ${worker.lastName || ''}`.trim()), value: worker.id })),
        [workers]
    )
    const clientNameById = useMemo(
        () => new Map(clients.map(client => [client.id, client.name || ''])),
        [clients]
    )
    const siteNameById = useMemo(
        () => new Map(sites.map(site => [site.id, site.name || ''])),
        [sites]
    )
    const workerNameById = useMemo(
        () => new Map(workers.map(worker => [worker.id, formatName(`${worker.firstName || ''} ${worker.lastName || ''}`).trim()])),
        [workers]
    )
    const timesheetSiteOptions = useMemo(
        () => sites
            .filter(site => !timesheetClientFilter || site.clientId === timesheetClientFilter.value)
            .map(site => ({ label: site.name, value: site.id })),
        [sites, timesheetClientFilter]
    )
    const filteredTimesheetRows = useMemo(
        () => timesheet
            .filter(entry => {
                const entryDate = getEntryDate(entry)
                if (!entryDate) return false
                const entryDateValue = new Date(entryDate)
                if (entryDateValue < weekRange.start || entryDateValue > weekRange.end) return false
                if (timesheetClientFilter && entry.chosenClient !== timesheetClientFilter.value) return false
                if (timesheetSiteFilter && entry.chosenSite !== timesheetSiteFilter.value) return false
                if (timesheetStatusFilter && getTimesheetDisplayStatus(entry) !== timesheetStatusFilter.value) return false
                return true
            })
            .map(row => ({
                ...row,
                client: clientNameById.get(row.chosenClient) || getDropdownLabel(row.chosenClient, clientOptions),
                site: siteNameById.get(row.chosenSite) || getDropdownLabel(row.chosenSite, siteOptions),
                worker: row.workerName || workerNameById.get(row.userId) || getDropdownLabel(row.userId, workerOptions),
            })),
        [timesheet, weekRange, timesheetClientFilter, timesheetSiteFilter, timesheetStatusFilter, clientNameById, siteNameById, workerNameById, clientOptions, siteOptions, workerOptions]
    )
    const timesheetSort = useTableSort(filteredTimesheetRows, { key: 'date', direction: 'ascending' });

    const handleWeekStartChange = (value) => {
        if (!value) return
        setStatsDate(startOfWeek(value, { weekStartsOn: 1 }))
    }

    function getEntryDate(entry) {
        return entry.date || entry.clockIn || entry.clockOut
    }

    const isWithinDateRange = (entry, startDate, endDate) => {
        const entryDate = getEntryDate(entry)
        if (!entryDate) return false
        const entryTime = new Date(entryDate).getTime()
        if (startDate) {
            const start = new Date(startDate)
            start.setHours(0, 0, 0, 0)
            if (entryTime < start.getTime()) return false
        }
        if (endDate) {
            const end = new Date(endDate)
            end.setHours(23, 59, 59, 999)
            if (entryTime > end.getTime()) return false
        }
        return true
    }

    const handleGenerateCsvLegacy = () => {
        const filtered = timesheet.filter(entry => isWithinDateRange(entry, reportStartDate, reportEndDate))
        if (!filtered.length) {
            toast.error('No timesheet entries found for the selected dates.')
            return
        }

        const rangeStart = reportStartDate ? new Date(reportStartDate) : null
        if (rangeStart) rangeStart.setHours(0, 0, 0, 0)
        const rangeEnd = reportEndDate ? new Date(reportEndDate) : null
        if (rangeEnd) rangeEnd.setHours(23, 59, 59, 999)
        const isDateInRange = (value) => {
            if (!value) return false
            const ts = new Date(value).getTime()
            if (Number.isNaN(ts)) return false
            if (rangeStart && ts < rangeStart.getTime()) return false
            if (rangeEnd && ts > rangeEnd.getTime()) return false
            return true
        }

        const workerMap = new Map(workers.map(worker => [worker.id, formatName(`${worker.firstName || ''} ${worker.lastName || ''}`).trim()]))
        const workerDetailsMap = new Map(workers.map(worker => [worker.id, worker]))
        const siteMap = new Map(sites.map(site => [site.id, site.name]))
        const grouped = new Map()

        filtered.forEach(entry => {
            const workerName = formatName(workerMap.get(entry.userId) || entry.worker || 'Unknown Worker')
            if (!grouped.has(workerName)) {
                grouped.set(workerName, [])
            }
            grouped.get(workerName).push(entry)
        })

        const normalizeRateForCsv = (rate) => {
            const numericRate = Number(rate)
            if (!Number.isFinite(numericRate)) return ''
            const cents = Math.round(numericRate * 100)
            const adjustedCents = cents % 10 === 1 ? cents - 1 : cents
            return (adjustedCents / 100).toFixed(2)
        }

        const normalizePaymentForCsv = (amount) => {
            const numericAmount = Number(amount)
            if (!Number.isFinite(numericAmount)) return ''
            // Business rule: payment values should be in $0.25 steps.
            return (Math.round((numericAmount + Number.EPSILON) * 4) / 4).toFixed(2)
        }

        const rows = []
        rows.push(['Worker Name', 'Date', 'Start Time', 'End Time', 'Hours', 'Break', 'Rate', 'Payment', 'Site Name', 'Date Paid'])

        Array.from(grouped.keys()).sort((a, b) => a.localeCompare(b)).forEach(workerName => {
            const entries = grouped.get(workerName).slice().sort((a, b) => {
                const aDate = new Date(getEntryDate(a) || 0).getTime()
                const bDate = new Date(getEntryDate(b) || 0).getTime()
                return aDate - bDate
            })

            const workerRows = []

            entries.forEach((entry) => {
                const workerDetails = workerDetailsMap.get(entry.userId)
                const workerPayrollInfo = getWorkerPayrollInfo(entry, workerDetails)
                const siteName = siteMap.get(entry.chosenSite) || entry.siteName || entry.site || ''
                const workerUserType = workerPayrollInfo.userType || workerDetails?.userType || ''
                const defaultCapacity = workerPayrollInfo.workerCapacity || getPrimaryWorkerType(workerPayrollInfo.workerTypes)

                if (workerUserType === 'Contractor') {
                    const segments = splitShiftByContractorRate(entry, workerPayrollInfo)
                    segments.forEach((segment) => {
                        const rowDate = segment.clockIn || entry.clockIn || entry.date || entry.clockOut
                        if (!isDateInRange(rowDate)) return

                        const paidHours = (segment.paidMinutes || 0) / 60
                        workerRows.push([
                            workerRows.length === 0 ? workerName : '',
                            rowDate ? format(new Date(rowDate), 'dd/MMM/yyyy') : '',
                            segment.clockIn ? formatTime(new Date(segment.clockIn)) : '',
                            segment.clockOut ? formatTime(new Date(segment.clockOut)) : '',
                            paidHours > 0 ? paidHours.toFixed(2) : '',
                            segment.unpaidBreakMinutes ? String(segment.unpaidBreakMinutes) : '',
                            segment.rate ? normalizeRateForCsv(segment.rate) : '',
                            paidHours > 0 ? normalizePaymentForCsv(paidHours * segment.rate) : '',
                            siteName,
                            '',
                        ])
                    })
                    return
                }

                const payRows = buildEmployeePayRows(entry, workerPayrollInfo)
                payRows.forEach((payRow, index) => {
                    const rowDate = payRow.clockIn || entry.clockIn || entry.date || entry.clockOut
                    if (!isDateInRange(rowDate)) return

                    workerRows.push([
                        index === 0 && workerRows.length === 0 ? workerName : '',
                        rowDate ? format(new Date(rowDate), 'dd/MMM/yyyy') : '',
                        payRow.clockIn ? formatTime(new Date(payRow.clockIn)) : '',
                        payRow.clockOut ? formatTime(new Date(payRow.clockOut)) : '',
                        typeof payRow.hours === 'number' ? payRow.hours.toFixed(2) : '',
                        index === 0 && entry.breakIncludedInPay === false && toNumber(entry.breakMinutes) > 0 ? String(toNumber(entry.breakMinutes)) : '',
                        payRow.rate ? normalizeRateForCsv(payRow.rate) : '',
                        normalizePaymentForCsv(payRow.payment),
                        siteName,
                        '',
                    ])
                })
            })
            if (workerRows.length) {
                const workerPaymentTotal = workerRows.reduce((sum, row) => {
                    const payment = Number(row[7])
                    return sum + (Number.isFinite(payment) ? payment : 0)
                }, 0)
                rows.push(...workerRows)
                rows.push([`${workerName} Total`, '', '', '', '', '', '', workerPaymentTotal.toFixed(2), '', ''])
                rows.push(['', '', '', '', '', '', '', '', '', ''])
                rows.push(['', '', '', '', '', '', '', '', '', ''])
            }
        })

        downloadCsvFile(rows, 'timesheet')
    }

    const handleGenerateCsv = async () => {
        if (!reportStartDate || !reportEndDate) {
            toast.error('Choose a start date and end date first.')
            return
        }
        try {
            setLoading(true)
            const resp = await postWithAuth('fetchWorkerPayrollReport', {
                orgId: activeOrg.id,
                startDate: format(reportStartDate, 'yyyy-MM-dd'),
                endDate: format(reportEndDate, 'yyyy-MM-dd'),
            })
            if (resp?.err) {
                toast.error(resp.err)
                return
            }
            const payrollRows = Array.isArray(resp?.data) ? resp.data : []
            if (!payrollRows.length) {
                toast.error('No completed timesheet entries found for the selected dates.')
                return
            }
            const rows = [['Worker Name', 'Date', 'Start Time', 'End Time', 'Hours', 'Break', 'Rate', 'Payment', 'Client Site Name', 'Date Paid']]
            const grouped = new Map()
            payrollRows.forEach(row => {
                const name = row.workerName || 'Unknown Worker'
                if (!grouped.has(name)) grouped.set(name, [])
                grouped.get(name).push(row)
            })
            Array.from(grouped.keys()).sort((a, b) => a.localeCompare(b)).forEach(workerName => {
                const workerRows = grouped.get(workerName)
                workerRows.forEach((row, index) => rows.push([
                    index === 0 ? workerName : '',
                    row.clockIn ? format(new Date(row.clockIn), 'dd/MMM/yyyy') : '',
                    row.clockIn ? formatTime(new Date(row.clockIn)) : '',
                    row.clockOut ? formatTime(new Date(row.clockOut)) : '',
                    Number(row.quantity || 0).toFixed(2),
                    row.breakMinutes ? String(row.breakMinutes) : '',
                    Number(row.rate || 0).toFixed(2),
                    Number(row.subtotal || 0).toFixed(2),
                    [row.siteName, row.clientName].filter(Boolean).join(' - '),
                    '',
                ]))
                const total = workerRows.reduce((sum, row) => sum + Number(row.subtotal || 0), 0)
                rows.push([`${workerName} Total`, '', '', '', '', '', '', total.toFixed(2), '', ''])
                rows.push(['', '', '', '', '', '', '', '', '', ''])
            })
            await downloadCsvFile(rows, 'worker_payroll')
        } catch (error) {
            toast.error(error?.message || 'Unable to generate worker payroll report.')
        } finally {
            setLoading(false)
        }
    }

    const handleGenerateClient = async () => {
        let exportRows
        try {
            const start = new Date(reportStartDate); start.setHours(0, 0, 0, 0)
            const end = new Date(reportEndDate); end.setHours(23, 59, 59, 999)
            const result = await postWithAuth('fetchTimeSheet', { orgId: activeOrg.id, rangeStart: start.toISOString(), rangeEnd: end.toISOString() })
            if (result?.err || !Array.isArray(result?.data)) throw new Error(result?.err || 'Could not load the selected dates.')
            exportRows = result.data
        } catch (error) { toast.error(error.message); return }
        const filtered = exportRows.filter(entry => isWithinDateRange(entry, reportStartDate, reportEndDate))
        if (!filtered.length) {
            toast.error('No timesheet entries found for the selected dates.')
            return
        }

        const clientMap = new Map(clients.map(client => [client.id, client.name]))
        const siteMap = new Map(sites.map(site => [site.id, site.name]))

        const groupedAllocations = new Map()
        filtered.forEach((entry) => {
            const entryDate = getEntryDate(entry)
            if (!entryDate) return

            const allocationKey = `${entry.chosenClient || entry.client || ''}__${entry.chosenSite || entry.site || ''}`
            if (!groupedAllocations.has(allocationKey)) {
                groupedAllocations.set(allocationKey, {
                    clientName: clientMap.get(entry.chosenClient) || entry.clientName || entry.client || '',
                    siteName: siteMap.get(entry.chosenSite) || entry.siteName || entry.site || '',
                    dates: new Map(),
                })
            }

            groupedAllocations.get(allocationKey).dates.set(format(new Date(entryDate), 'dd/MMM/yyyy'), entryDate)
        })

        const rows = [['Client', 'Site', 'Date']]
        Array.from(groupedAllocations.values())
            .map((row) => ({
                clientName: row.clientName,
                siteName: row.siteName,
                dates: Array.from(row.dates.entries())
                    .sort((a, b) => new Date(a[1] || 0).getTime() - new Date(b[1] || 0).getTime())
                    .map(([label]) => label),
            }))
            .sort((a, b) => {
                const clientCompare = (a.clientName || '').localeCompare(b.clientName || '')
                if (clientCompare !== 0) return clientCompare
                const siteCompare = (a.siteName || '').localeCompare(b.siteName || '')
                if (siteCompare !== 0) return siteCompare
                return (a.dates[0] || '').localeCompare(b.dates[0] || '')
            })
            .forEach((row) => {
                rows.push([
                    row.clientName,
                    row.siteName,
                    row.dates.join(', '),
                ])
            })

        downloadCsvFile(rows, 'client_invoice_report')
    }

    const handleSaveTimeSheet = async () => {


        try {
            setLoading(true)
            const breakValue = Number(breakMinutes) || 0;
            if (breakValue < 0 || breakValue % 15 !== 0) {
                toast.error('Break duration must be a non-negative multiple of 15 minutes.')
                setLoading(false)
                return
            }
            if (!clockIn && clockOut) {
                toast.error('Shift Start is required when Shift End is entered.')
                setLoading(false)
                return
            }

            const resettingTimesheet = !clockIn && !clockOut

            const payload = {
                id: editId,
                clockIn: clockIn ? businessWallDateToIso(clockIn) : '',
                clockOut: clockOut ? businessWallDateToIso(clockOut) : '',
                orgId: activeOrg.id,
                breakMinutes: resettingTimesheet ? 0 : breakValue,
                breakIncludedInPay: resettingTimesheet ? false : !!breakIncludedInPay,
                travelAllowanceApplied: resettingTimesheet ? false : !!travelAllowanceApplied,
            }

            const supervisorPayload = {
                supervisorName: resettingTimesheet ? '' : viewTimeSheet.supervisorName || '',
                supervisorNotAvailable: resettingTimesheet ? false : !!viewTimeSheet.supervisorNotAvailable,
                supervisorSign: resettingTimesheet ? '' : viewTimeSheet.supervisorSign || ''
            }
            const resp = await postWithAuth('saveTimeSheet', { ...payload, ...supervisorPayload })
            if (resp?.err) {
                throw new Error(resp.err)
            }

            setIsTimesheetDirty(false)
            setShowTimesheetSuccess(true)
            setLoading(false)
            fetchTimeSheet()

        } catch (e) {
            toast.error(e.message)
            setLoading(false)
        }
    }


    const handleView = async (timeSheet, options = {}) => {
        const { syncUrl = true } = options
        try {
            const [detailedTimeSheet] = await fetchTimeSheetDetails({ timeSheetId: timeSheet.id })
            if (!detailedTimeSheet) {
                toast.error('Timesheet not found.')
                return
            }
            if (syncUrl) {
                navigate(`/timesheet?timesheetId=${timeSheet.id}`)
                lastOpenedRef.current = timeSheet.id
            }
            setViewTimeSheet(detailedTimeSheet)
            setClockIn(detailedTimeSheet.clockIn ? toBusinessWallDate(detailedTimeSheet.clockIn) : '')
            setClockOut(detailedTimeSheet.clockOut ? toBusinessWallDate(detailedTimeSheet.clockOut) : '')
            setBreakMinutes(detailedTimeSheet.breakMinutes !== undefined ? String(detailedTimeSheet.breakMinutes) : '0')
            setTravelAllowanceApplied(!!detailedTimeSheet.travelAllowanceApplied)
            setIsTimesheetDirty(false)
            setShowTimesheetSuccess(false)
            setIsAdding(true)
            setEditId(detailedTimeSheet.id)
        } catch (e) {
            toast.error(e.message)
            return
        }
        setBreakIncludedInPay(false)
    }

    const clearFields = () => {
        setViewTimeSheet('')
        setClockIn('')
        setClockOut('')
        setEditId('')
        setBreakMinutes('0')
        setTravelAllowanceApplied(false)
        setIsTimesheetDirty(false)
        setShowTimesheetSuccess(false)
    }

    const closeTimesheetView = () => {
        const returnTo = location.state?.returnTo
        clearFields()
        setIsAdding(false)
        lastOpenedRef.current = null
        if (returnTo) {
            navigate(returnTo, { replace: true })
            return
        }
        if (location.search) {
            navigate('/timesheet', { replace: true })
        }
    }

    const formatWorkerDisplayName = (worker) => {
        const name = String(worker?.name || '').trim()
        const role = String(worker?.role || '').trim()
        return role ? `${name} (${role})` : name
    }

    const getSelectedTimesheetWorker = (entry) => {
        if (!entry) return null
        const allocatedWorker = (entry.chosenWorkerNames || []).find(worker => worker.id === entry.userId)
        if (allocatedWorker) return allocatedWorker

        const worker = workers.find(item => item.id === entry.userId)
        if (!worker) return null
        return {
            id: worker.id,
            name: formatName(`${worker.firstName || ''} ${worker.lastName || ''}`).trim(),
            role: entry.workerCapacity || (Array.isArray(worker.workerType) ? worker.workerType[0] || '' : ''),
            assetName: '',
        }
    }

    const updateTimesheetClockIn = (value) => {
        setClockIn(value)
        setIsTimesheetDirty(true)
    }

    const updateTimesheetClockOut = (value) => {
        setClockOut(value)
        setIsTimesheetDirty(true)
    }

    const updateTimesheetBreakMinutes = (value) => {
        setBreakMinutes(value)
        setIsTimesheetDirty(true)
    }

    return <div>
        <Header />
        <LoadingPopup visible={loading} message="Loading..." />
        {downloadTimeSheet && <TimesheetPdfGenerator
            timesheet={downloadTimeSheet}
            workerName={getSelectedTimesheetWorker(downloadTimeSheet)?.name || ''}
            logo={activeOrgInfo?.logo?.url}
            onComplete={() => setDownloadTimeSheet(null)}
        />}
        <div className='pageShell'>
            <Sidebar />
            <PageContent as="div" className='pageContent pageContentPadded timesheetPageContent'>
                <PageHeader title={<>Timesheets</>} subtitle={<>Review shifts and generate site reports</>} />
                {/* Timesheet detail opens as a dialog over the list (matches the reference design). */}
                <ModalComponent
                    showModal={isAdding}
                    setShowModal={(next) => { if (!next) closeTimesheetView() }}
                    customMaxWidth={showTimesheetSuccess ? '360px' : '740px'}
                    customContentStyle={showTimesheetSuccess
                        ? { minWidth: 0, width: 'min(360px, calc(100vw - 32px))', padding: 0, overflow: 'hidden' }
                        : { minWidth: 0, width: 'min(740px, calc(100vw - 32px))', height: 'auto', maxHeight: 'calc(100vh - 24px)', padding: 0, overflow: 'hidden' }}
                >
                    {showTimesheetSuccess ? (
                        <div className='timesheetSuccessPage'>
                            <div className='timesheetSuccessContent'>
                                <div className='timesheetSuccessIcon' aria-hidden='true'>
                                    <svg viewBox='0 0 24 24'>
                                        <path d='M5 12.5l4.2 4.2L19 7' />
                                    </svg>
                                </div>
                                <h2>Timesheet saved</h2>
                                <p>Timesheet details saved successfully.</p>
                            </div>
                            <div className='timesheetSuccessFooter'>
                                <Button variant="secondary" size="md" className='timesheetPrimaryButton' onClick={closeTimesheetView}>Cancel</Button>
                            </div>
                        </div>
                    ) : (
                    <div className='timesheetDialogShell'>
                        <div className='timesheetDetailHeader'>
                            <div>
                                <h2 className='timesheetDetailTitle'>Timesheet Details</h2>
                            </div>
                            {viewTimeSheet && (
                                <span className={`timesheetDetailStatus timesheetDetailStatus${getTimesheetDisplayStatus(viewTimeSheet)}`}>
                                    {getTimesheetDisplayStatus(viewTimeSheet)}
                                </span>
                            )}
                        </div>
                        <div className='timesheetDialogBody'>
                            {viewTimeSheet && <>
                                <SectionCard className='timesheetDetailCard timesheetJobCard' title={<>Job</>}>

                                    <div className='timesheetJobGrid'>
                                        <div className='detailItem'><span className='detailLabel'>Allocation ID</span><span className='detailValue'>{viewTimeSheet.allocationNumber || viewTimeSheet.allocationId || '-'}</span></div>
                                        <div className='detailItem'><span className='detailLabel'>Date</span><span className='detailValue'>{viewTimeSheet.date ? format(new Date(viewTimeSheet.date), 'EEE, d MMM yyyy') : '-'}</span></div>
                                        <div className='detailItem'><span className='detailLabel'>Client Name</span><span className='detailValue'>{viewTimeSheet.clientName || '-'}</span></div>
                                        <div className='detailItem'><span className='detailLabel'>Site Name</span><span className='detailValue'>{viewTimeSheet.siteName || '-'}</span></div>
                                        <div className='detailItem'><span className='detailLabel'>Location Address</span><span className='detailValue'>{getAllocationDisplayAddress(viewTimeSheet)}</span></div>
                                        <div className='detailItem'><span className='detailLabel'>Scope of Works</span><span className='detailValue'>{viewTimeSheet.scopeOfWorks || '-'}</span></div>
                                    </div>
                                </SectionCard>

                                <div className='timesheetDetailMiddleGrid'>
                                    <SectionCard className='timesheetDetailCard' title={<>Workers</>}>

                                        {(() => {
                                            const selectedWorker = getSelectedTimesheetWorker(viewTimeSheet)
                                            return selectedWorker ? (
                                                <div className='timesheetDetailWorkerList'>
                                                    <div className='timesheetDetailWorkerName'>
                                                        {formatWorkerDisplayName(selectedWorker)}
                                                    </div>
                                                </div>
                                            ) : <p className='modalEmpty'>No worker found for this timesheet.</p>
                                        })()}

                                        <h3 className='timesheetDetailAssetsTitle'>Assets</h3>
                                        {(() => {
                                            const selectedWorker = getSelectedTimesheetWorker(viewTimeSheet)
                                            const assetNames = String(selectedWorker?.assetName || '')
                                                .split(',')
                                                .map(name => name.trim())
                                                .filter(Boolean)
                                            return assetNames.length > 0
                                                ? <div className='timesheetDetailAssetList'>{assetNames.map((a, i) => <div key={`ts-asset-${i}`}>{a}</div>)}</div>
                                                : <p className='modalEmpty'>No assets used</p>
                                        })()}
                                    </SectionCard>

                                    <SectionCard className='timesheetDetailCard' title={<>Site Supervisor</>} actions={<><Checkbox disabled value={viewTimeSheet.supervisorNotAvailable} setValue={() => {}} label={'No one on site to sign off'} /></>}>

                                        <div className='detailItem timesheetSupervisorName'>
                                            <span className='detailLabel'>Supervisor Name</span>
                                            <span className='detailValue'>{formatName(viewTimeSheet.supervisorName || '-')}</span>
                                        </div>
                                        {!viewTimeSheet.supervisorNotAvailable && viewTimeSheet.supervisorSign && (
                                            <div className='timesheetSignatureBlock'>
                                                <img src={viewTimeSheet.supervisorSign} alt='Supervisor signature' className='signaturePreview' />
                                            </div>
                                        )}
                                    </SectionCard>
                                </div>

                                <SectionCard className='timesheetDetailCard timesheetDetailShiftCard' title={<>Shift Details</>}>

                                    <div className='timesheetDetailShiftGrid'>
                                        <MyCalendar noMaxDate disabled={false} startDate={clockIn} setStartDate={updateTimesheetClockIn} label={'Shift Start'} timeIntervals={15} dateFormat='d MMMM yyyy · h:mm a' calendarClassName='timesheetCompactDateTimePicker' />
                                        <MyCalendar noMaxDate disabled={false} startDate={clockOut} setStartDate={updateTimesheetClockOut} label={'Shift End'} timeIntervals={15} dateFormat='d MMMM yyyy · h:mm a' calendarClassName='timesheetCompactDateTimePicker' />
                                        <div className='timesheetBreakPicker'>
                                            <Dropdown
                                                disabled={false}
                                                label={'Break (mins)'}
                                                options={breakMinuteOptions}
                                                value={breakMinuteOptions.find(option => option.value === String(breakMinutes)) || breakMinuteOptions[0]}
                                                setValue={(option) => updateTimesheetBreakMinutes(option?.value || '0')}
                                                isSearchable={false}
                                                menuPlacement='auto'
                                            />
                                        </div>
                                    {clockIn && clockOut &&
                                        <div className='timesheetHoursField'>
                                            <Input disabled={true} type={'number'} label={'Hours'} value={getQuarterHoursFromTimes({ clockIn, clockOut, breakMinutes, breakIncludedInPay })} />
                                        </div>
                                    }
                                    </div>
                                </SectionCard>
                                <TimesheetChangeHistory orgId={activeOrg?.id} timeSheet={viewTimeSheet} />
                            </>}
                        </div>

                        <div className='timesheetDetailFooter'>
                            <Button variant="secondary" size="md" className='secondaryButton timesheetDetailCloseButton' onClick={closeTimesheetView}>
                                Cancel
                            </Button>
                            {viewTimeSheet.clockIn && viewTimeSheet.clockOut && (
                                <Button variant="secondary" size="md" className='secondaryButton' onClick={() => setDownloadTimeSheet(viewTimeSheet)}>
                                    Download PDF
                                </Button>
                            )}
                            {isTimesheetDirty && (
                                <Button data-action-tone="save" variant="soft" size="md"  className='timesheetPrimaryButton timesheetDetailSaveButton saveWithIcon' disabled={loading || !clockIn} onClick={handleSaveTimeSheet}>Save</Button>
                            )}
                        </div>
                    </div>
                    )}

                </ModalComponent>

                    <Section title='Reports' className="width-100 timesheetReportCard timesheetHeaderTint" variant='card'>
                        <div className='timesheetUnifiedReportFields'>
                            <div>
                                <FieldLabel htmlFor='timesheet-report-type'>Type</FieldLabel>
                                <UiSelect id='timesheet-report-type' value={reportType} onChange={event => setReportType(event.target.value)}>
                                    <option value='worker'>Worker Site</option>
                                    <option value='client'>Client Site</option>
                                </UiSelect>
                            </div>
                            <MyCalendar noMaxDate showDateOnly disabled={false} startDate={reportStartDate} setStartDate={setReportStartDate} label='Start Date' />
                            <MyCalendar noMaxDate showDateOnly disabled={false} startDate={reportEndDate} setStartDate={setReportEndDate} label='End Date' />
                        </div>
                        <div className='timesheetReportActions'>
                            {(reportType === 'client' || !userInfo?.isStaff) && <Button variant="create" size="md" className='timesheetPrimaryButton' onClick={reportType === 'worker' ? handleGenerateCsv : handleGenerateClient}>Generate</Button>}
                            <Button variant="secondary" size="md" className='secondaryButton' onClick={() => { setReportStartDate(''); setReportEndDate('') }}>Clear Filters</Button>
                        </div>
                    </Section>

                    <Section title={'Timesheets'} className="width-100 timesheetListSection timesheetHeaderTint" variant='card'>

                            <div className='timesheetToolbarGrid'>
                                <div className='timesheetWeekGrid'>
                                    <MyCalendar noMaxDate showDateOnly dateFormat='EEE, d MMM yyyy' disabled={false} startDate={weekRange.start} setStartDate={handleWeekStartChange} label={'Week Start'} />
                                    <div className='timesheetReadOnlyField'>
                                        <FieldLabel className='fieldLabel'>Week End</FieldLabel>
                                        <UiInput disabled value={format(weekRange.end, 'EEE, d MMM yyyy').replace(/^Thu,/, 'Thur,')} aria-label='Week End' />
                                    </div>
                                </div>

                                <div className='timesheetFilterGrid'>
                                    <div className='timesheetFilterField'>
                                        <Dropdown
                                            clearable
                                            options={clientOptions}
                                            value={timesheetClientFilter}
                                            setValue={(value) => {
                                                setTimesheetClientFilter(value)
                                                setTimesheetSiteFilter('')
                                            }}
                                            placeholder={'All clients'}
                                        />
                                    </div>
                                    <div className='timesheetFilterField'>
                                        <Dropdown
                                            clearable
                                            options={timesheetSiteOptions}
                                            value={timesheetSiteFilter}
                                            setValue={setTimesheetSiteFilter}
                                            placeholder={'All sites'}
                                        />
                                    </div>
                                    <div className='timesheetFilterField'>
                                        <Dropdown
                                            clearable
                                            options={timesheetStatusOptions}
                                            value={timesheetStatusFilter}
                                            setValue={setTimesheetStatusFilter}
                                            placeholder={'All statuses'}
                                        />
                                    </div>
                                    <Button variant="secondary" size="md" className="secondaryButton timesheetClearButton" onClick={() => { setTimesheetClientFilter(''); setTimesheetSiteFilter(''); setTimesheetStatusFilter('') }}>Clear Filters</Button>
                                </div>
                            </div>

                            <div className='timesheetTableWrap'>
                            <div className='timesheetTableHeadingDivider' />
                            <Table className='timesheetListTable'>
                                <TableHead>
                                    <TableRow>

                                        <TableHeaderCell {...timesheetSort.getSortableHeaderProps('date')}>
                                            Date/Time
                                        </TableHeaderCell>
                                        <TableHeaderCell>
                                            Allocation ID
                                        </TableHeaderCell>
                                        <TableHeaderCell {...timesheetSort.getSortableHeaderProps('client')}>
                                            Client
                                        </TableHeaderCell>
                                        <TableHeaderCell {...timesheetSort.getSortableHeaderProps('site')}>
                                            Site
                                        </TableHeaderCell>
                                        <TableHeaderCell {...timesheetSort.getSortableHeaderProps('worker')}>
                                            Worker
                                        </TableHeaderCell>

                                        <TableHeaderCell>
                                            Shift Start
                                        </TableHeaderCell>
                                        <TableHeaderCell>
                                            Shift End
                                        </TableHeaderCell>
                                        <TableHeaderCell>
                                            Status
                                        </TableHeaderCell>
                                    </TableRow>
                                </TableHead>
                                <tbody>
                                    {timesheetSort.sortedData.map(timeSheet => {
                                        const status = getTimesheetDisplayStatus(timeSheet)
                                        const rowClassName = status === 'Completed'
                                            ? 'timesheetStatusCompleted'
                                            : status === 'Accepted'
                                                ? 'timesheetStatusApproved'
                                                : 'timesheetStatusIncomplete'
                                        return (<TableRow key={timeSheet.id} className={rowClassName}>
                                            <TableCell className={rowClassName}>
                                                <a
                                                    href={`/timesheet?timesheetId=${timeSheet.id}`}
                                                    onClick={(event) => {
                                                        if (
                                                            event.button !== 0 ||
                                                            event.metaKey ||
                                                            event.ctrlKey ||
                                                            event.shiftKey ||
                                                            event.altKey
                                                        ) {
                                                            return
                                                        }
                                                        event.preventDefault()
                                                        handleView(timeSheet)
                                                    }}
                                                    className="hover timesheetDateLink"
                                                >
                                                    <span>{format(new Date(timeSheet.date), 'EEE, d MMM yyyy').replace(/^Thu,/, 'Thur,')}</span>
                                                    <span>{formatTime(new Date(timeSheet.date))}</span>
                                                </a>
                                            </TableCell>
                                            <TableCell>{timeSheet.allocationNumber}</TableCell>
                                            <TableCell>{timeSheet.client}</TableCell>
                                            <TableCell>{timeSheet.site}</TableCell>
                                            <TableCell>{timeSheet.worker}</TableCell>
                                            <TableCell>{timeSheet.clockIn ? <>{format(new Date(timeSheet.clockIn), 'EEE, d MMM yyyy')}<br />{formatTime(new Date(timeSheet.clockIn))}</> : ''}</TableCell>
                                            <TableCell>{timeSheet.clockOut ? <>{format(new Date(timeSheet.clockOut), 'EEE, d MMM yyyy')}<br />{formatTime(new Date(timeSheet.clockOut))}</> : ''}</TableCell>
                                            <TableCell className={rowClassName}>{status}{timeSheet.clientEdit && <><br /><span className='timesheetClientEditBadge' title={`${formatName(timeSheet.clientEdit.actorName) || 'A client contact'} changed the hours when signing off in Site Hub`}>Edited by client</span></>}</TableCell>
                                        </TableRow>)
                                    })}
                                    {timesheetSort.sortedData.length === 0 && <TableEmpty  colSpan={8}>No timesheets yet.</TableEmpty>}
                                </tbody>
                            </Table>
                            </div>
                    </Section>
            </PageContent>
        </div>
    </div >
}

export default Timesheet
